<template>
    <div>
        <div v-if="!fetchingOrgUsers" class="d-flex flex-wrap align-center">
            <OrganizationInvite v-if="isOrgManager && orgUsers.length" />
            <v-spacer />
            <v-text-field
                class="mx-2"
                v-show="expand && orgUsers.length"
                placeholder="Search for a user..."
                autofocus
                prepend-inner-icon="mdi-magnify "
                v-model="search"
                solo
                flat
                hide-details
                dense>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <div v-for="(item, index) in Object.entries(userCheckboxBinders)" :key="index">
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item[1].status">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>{{ item[1].icon }}</v-icon>
                                                <span>{{ capitalize(item[0]) }}</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </div>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" @click="$store.dispatch('orgStore/fetchOrgUsers', $route.params.oid)" text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>refresh</span>
            </v-tooltip>
        </div>

        <div v-if="!fetchingOrgUsers">
            <v-data-table
                v-if="orgUsers.length"
                :headers="userHeaders"
                :items="filteredOrgUsers"
                :search="search"
                item-key="uid"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <div class="font-weight-medium py-2">{{ item.full_name || '-' }}</div>
                </template>
                <template v-slot:[`item.email`]="{ item }">
                    <div class="py-2">{{ item.email || '-' }}</div>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <div class="py-2">
                        <v-chip v-if="item.is_active === true" outlined label small>
                            <v-icon left x-small color="success">mdi-circle</v-icon>
                            <span>Active</span>
                        </v-chip>
                        <v-chip v-else-if="item.is_active === false" outlined label small>
                            <v-icon left x-small color="error">mdi-circle</v-icon>
                            <span>Inactive</span>
                        </v-chip>
                        <span v-else>-</span>
                    </div>
                </template>
                <template v-slot:[`item.display_role`]="{ item }">
                    <div class="d-flex align-center py-2">
                        <v-chip outlined label small>
                            <v-icon left small>{{ organizationRole(item.display_role).icon }}</v-icon>
                            <span>{{ capitalize(organizationRole(item.display_role).label) }}</span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:[`item.joined_at`]="{ item }">
                    <span v-if="item.joined_at" class="text-no-wrap">{{ item.joined_at | dateTimeToHuman }}</span>
                    <span v-else>&mdash;</span>
                </template>
                <template v-slot:[`item.invited_by`]="{ item }">
                    <span>{{ item.invited_by || '\u2014' }}</span>
                </template>
                <template v-slot:[`item.invited_at`]="{ item }">
                    <span v-if="item.invited_at" class="text-no-wrap">{{ item.invited_at | dateTimeToHuman }}</span>
                    <span v-else>&mdash;</span>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu
                        v-if="isOrgManager"
                        :value="openActionsMenuUid === item.uid"
                        @input="toggleActionsMenu(item.uid, $event)"
                        left
                        offset-x
                        nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <OrgUserRoleDialog :user="item" activatorType="listItem" @close-menu="closeActionsMenu" />
                            <v-divider class="my-2" />
                            <UserActions
                                :ucid="item.ucid.toString()"
                                :userName="item.full_name"
                                :hierarchicalLevel="nuvolosObjectTypes.ORGANIZATION"
                                :role="item.role"
                                :orgName="$store.getters['orgStore/orgLongName']"
                                activatorType="listItem"
                                @close-menu="closeActionsMenu"></UserActions>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-results>
                    <span>No users found.</span>
                </template>
                <template v-slot:no-data>
                    <span>No users found. Consider inviting new users via the "+ user" button above.</span>
                </template>
            </v-data-table>

            <div v-else>
                <v-banner two-line>
                    <v-avatar slot="icon" color="white" size="60">
                        <v-icon x-large color="info">mdi-information-outline</v-icon>
                    </v-avatar>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold secondary--text mb-1">No users found</span>
                        <span>
                            Currently this organization has no additional users.
                            <span v-if="isOrgManager">
                                You can add new organization users via the
                                <span class="font-weight-bold secondary--text">+ user</span>
                                button above.
                            </span>
                        </span>
                    </div>
                </v-banner>
            </div>

            <v-alert :value="this.error" color="error" icon="mdi-alert-outline" class="mt-4" text>
                <span class="font-weight-bold">An error has occurred</span>
                <div>
                    The user could not be revoked. To learn more about user revoke issues, check the troubleshooting documentation
                    <v-btn
                        small
                        outlined
                        text
                        color="error"
                        href="https://docs.nuvolos.com/troubleshooting/troubleshooting/administration-troubleshooting/i-cant-revoke-a-user-role"
                        target="_blank">
                        here
                    </v-btn>
                </div>
            </v-alert>
        </div>
        <div v-else>
            <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { capitalize } from 'lodash'
import { enumsData } from '@/mixins/enums'
import { organizationRole, userDisplayRole } from '@/utils'
import OrganizationInvite from '@/modules/home/components/OrganizationInvite.vue'
const UserActions = () => import('@/components/UserActions')
const OrgUserRoleDialog = () => import('@/modules/home/components/OrgUserRoleDialog.vue')

export default {
    name: 'OrganizationUsers',
    components: { UserActions, OrganizationInvite, OrgUserRoleDialog },
    mixins: [enumsData],
    data() {
        return {
            search: '',
            error: false,
            expand: true,
            userName: '',
            userUcid: '',
            openActionsMenuUid: null,
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            userCheckboxBinders: {
                owners: { status: true, icon: organizationRole('owner').icon },
                managers: { status: true, icon: organizationRole('manager').icon },
                faculty: { status: true, icon: organizationRole('faculty').icon },
                members: { status: true, icon: organizationRole('member').icon }
            }
        }
    },
    computed: {
        ...mapState('orgStore', ['orgUsers', 'fetchingOrgUsers']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        ...mapGetters('orgStore', ['isOrgManager']),
        userHeaders() {
            const baseHeaders = [
                { text: 'User', value: 'user', sortable: false },
                { text: 'Email', value: 'email', sortable: false },
                { text: 'Status', value: 'status', sortable: false, width: '120px' },
                { text: 'User type', value: 'display_role', sortable: false },
                { text: 'Joined', value: 'joined_at', sortable: true },
                { text: 'Invited by', value: 'invited_by', sortable: true },
                { text: 'Invited on', value: 'invited_at', sortable: true }
            ]
            if (this.isOrgManager) {
                baseHeaders.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '120px' })
            }
            return baseHeaders
        },
        filteredOrgUsers() {
            if (this.orgUsers) {
                const displayedUsers = []
                if (this.userCheckboxBinders.owners.status) {
                    displayedUsers.push('OWNER')
                }
                if (this.userCheckboxBinders.managers.status) {
                    displayedUsers.push(this.roleTypes.ORG_MANAGER)
                }
                if (this.userCheckboxBinders.faculty.status) {
                    displayedUsers.push(this.roleTypes.ORG_FACULTY)
                }
                if (this.userCheckboxBinders.members.status) {
                    displayedUsers.push(this.roleTypes.ORG_MEMBER)
                }
                const filteredUsers = this.orgUsers
                    .map(user => ({ ...user, display_role: userDisplayRole(user) }))
                    .filter(user => displayedUsers.includes(user.display_role))
                return filteredUsers
            }
            return []
        }
    },
    methods: {
        capitalize,
        organizationRole,
        toggleActionsMenu(uid, isOpen) {
            this.openActionsMenuUid = isOpen ? uid : null
        },
        closeActionsMenu() {
            this.openActionsMenuUid = null
        }
    }
}
</script>
